import Image from '@/components/Image'
import Features from '@/components/MachineCardV2/components/Features'
import { PromotionalTag } from '@/components/PromotionalTag'
import RetainQueryLinkDefault from '@/components/RetainQueryLink/RetainQueryLinkDefault'
import IS_SERVER from '@/constants/isServer'
import { useAmplitude } from '@/contexts/AmplitudeProvider'
import { MachineDetailsFromAPI, useTonMachines } from '@/contexts/machines'
import { useTonPlans } from '@/contexts/plans'
import { useGetCheckoutLink } from '@/hooks/useGetCheckoutLink'
import { useGetMachinePriceDetails } from '@/hooks/useGetMachinePriceDetails'
import { IPromotionalTag } from '@/types/cms'
import cn from '@/utils/cn'

interface CardMachineLargeProps extends MachineDetailsFromAPI {
  className?: string
  promotionalTag: IPromotionalTag
}

const CardMachineLarge = ({
  id,
  refID,
  className,
  original_price,
  amount,
  discount_percentage,
  promotionalTag
}: CardMachineLargeProps) => {
  const { currentPlan } = useTonPlans()
  const { getCheckoutLink } = useGetCheckoutLink()
  const { machinesDetailsByRefID } = useTonMachines()
  const { getMachinePriceDetails } = useGetMachinePriceDetails()

  const machineDetails = machinesDetailsByRefID.get(refID)

  const { analytics } = useAmplitude()

  if (!machineDetails || !currentPlan) return null

  const linkToCheckout = getCheckoutLink(id)
  const planPopularName =
    currentPlan.commercial_name.length <= 11
      ? currentPlan.commercial_name
      : currentPlan.short_commercial_name
  const machineImageName = machineDetails.image
  const machinePopularName = machineDetails.name

  const { sellingPrice, origionalPrice, installmentValue, installmentParcel } =
    getMachinePriceDetails({
      id,
      refID,
      amount,
      original_price
    })

  return (
    <div
      className={cn(
        'flex bg-display-0 p-24 gap-40 rounded-24 shadow-new max-w-full',
        className
      )}
    >
      <div className="flex flex-col min-w-[245px] min-h-[445px]">
        <div className="absolute mt-40 max-w-[245px]">
          <Image
            src={`ton-cms/prd/${machineImageName.left}.webp`}
            alt={`Imagem da maquininha ${machinePopularName}`}
            width={245}
            height={368}
            className="object-cover max-w-full h-auto"
          />

          {promotionalTag && (
            <PromotionalTag
              className="absolute top-[-36px] left-[108px] w-[145px] h-[83px]"
              value={discount_percentage}
              variant={promotionalTag.variant}
            />
          )}
        </div>

        <div className="flex relative top-[367px] justify-end ml-auto">
          {machineDetails.badges?.map((badge, index) => (
            <div
              className={cn(
                'ml-[-13px] first:z-40',
                machineDetails.badges?.length > 1 &&
                  'max-w-[81px] max-h-[81px] md:max-w-[81px] md:max-h-[81px]'
              )}
              key={`img-name-${badge}-${index}`}
            >
              <Image
                src={`site-ton/${badge}1.png`}
                alt={`Icone representando a feature ${badge}`}
                width={81}
                height={81}
                className="object-cover max-w-full h-auto rounded-full"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-16 justify-between w-full">
        <h3 className="font-extrabold">
          {machinePopularName}{' '}
          <span className="font-light">{planPopularName}</span>
        </h3>
        <div className="flex flex-col justify-between">
          <div className="flex">
            <div className="mr-8 md:mr-16">
              <p className="paragraph-14">
                <s>R$ {origionalPrice}</s>
              </p>
              <p className="text-newTon-700">
                <b>R$ {sellingPrice}</b>
              </p>
              <p className="paragraph-14">à vista ou</p>
            </div>
            <div className="flex justify-end my-auto ml-auto font-heading font-bold text-newTon-700 paragraph-14 md:paragraph-16">
              <div className="self-end mr-4 text-right">
                <p className="leading-none text-display-900 paragraph-18">
                  {installmentParcel}x
                </p>
                <p className="mt-4 leading-none text-current paragraph-18">
                  R$
                </p>
              </div>
              <p className="self-end text-[78px] leading-[58px] text-current">
                {installmentValue?.split(',')[0]}
              </p>
              <p className="leading-[20px] text-current paragraph-18">
                ,{installmentValue?.split(',')[1]}
              </p>
            </div>
          </div>
        </div>
        {linkToCheckout && (
          <RetainQueryLinkDefault
            href={linkToCheckout}
            id={`btn-pedir-maquininha-${refID}`}
            className="w-full font-bold capitalize bg-newTon-400 btn btn-primary btn-large"
            rel="noreferrer"
            onClick={async () => {
              if (IS_SERVER) return

              analytics?.track({
                event_type: `choose prod ton`,
                event_properties: {
                  name: `choose prod ton`,
                  description: `Evento disparado quando usuario clicar no cta de pedir maquininha`,
                  section_reference: `Escolha sua maquininha`,
                  cta_reference: `Pedir ${machinePopularName} ${planPopularName}`
                }
              })
            }}
          >
            Pedir {machinePopularName}
          </RetainQueryLinkDefault>
        )}
        <div className="flex flex-col md:flex-1">
          <div className="flex-1">
            <Features
              machine={machinePopularName}
              features={machineDetails.features}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
export default CardMachineLarge
